import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ClipboardList,
  Plus,
  Edit2,
  Trash2,
  Copy,
  Check,
  X,
  GripVertical,
  RefreshCw,
  ArrowLeft,
  Star,
  MessageSquare,
  BarChart3
} from 'lucide-react';

// Default question categories
const questionCategories = [
  { id: 'performance', label: 'Performance', color: 'bg-blue-100 text-blue-700' },
  { id: 'competences', label: 'Compétences', color: 'bg-purple-100 text-purple-700' },
  { id: 'comportement', label: 'Comportement', color: 'bg-green-100 text-green-700' },
  { id: 'leadership', label: 'Leadership', color: 'bg-orange-100 text-orange-700' },
  { id: 'custom', label: 'Personnalisé', color: 'bg-slate-100 text-slate-700' }
];

// Question types
const questionTypes = [
  { id: 'rating', label: 'Note (1-5 étoiles)' },
  { id: 'scale_10', label: 'Échelle (1-10)' },
  { id: 'text', label: 'Texte libre' },
  { id: 'yes_no', label: 'Oui / Non' }
];

export default function EvaluationTemplatesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    targetType: 'all',
    targetIds: [],
    isDefault: false,
    questions: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, deptsRes] = await Promise.all([
        api.get(`/evaluation-templates?company_id=${user?.companyId}&include_inactive=true`),
        api.get(`/departments?companyId=${user?.companyId}`)
      ]);
      setTemplates(templatesRes.data.templates || []);
      setDepartments(deptsRes.data.departments || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      targetType: 'all',
      targetIds: [],
      isDefault: false,
      questions: [
        {
          question: '',
          description: '',
          category: 'performance',
          type: 'rating',
          required: true,
          weight: 1
        }
      ]
    });
    setShowCreateDialog(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      targetType: template.targetType || 'all',
      targetIds: template.targetIds || [],
      isDefault: template.isDefault || false,
      questions: template.questions || []
    });
    setShowCreateDialog(true);
  };

  const handleDuplicate = async (template) => {
    setEditingTemplate(null);
    setFormData({
      name: `${template.name} (copie)`,
      description: template.description || '',
      targetType: template.targetType || 'all',
      targetIds: template.targetIds || [],
      isDefault: false,
      questions: template.questions?.map(q => ({ ...q, id: undefined })) || []
    });
    setShowCreateDialog(true);
  };

  const handleDelete = async (templateId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce questionnaire ?')) return;
    
    try {
      await api.delete(`/evaluation-templates/${templateId}?company_id=${user?.companyId}`);
      toast.success('Questionnaire supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Veuillez saisir un nom pour le questionnaire');
      return;
    }
    
    if (formData.questions.length === 0) {
      toast.error('Ajoutez au moins une question');
      return;
    }
    
    // Validate questions
    const emptyQuestions = formData.questions.filter(q => !q.question.trim());
    if (emptyQuestions.length > 0) {
      toast.error('Toutes les questions doivent avoir un intitulé');
      return;
    }
    
    setSaving(true);
    try {
      if (editingTemplate) {
        await api.put(
          `/evaluation-templates/${editingTemplate.id}?company_id=${user?.companyId}&updated_by=${user?.id}`,
          formData
        );
        toast.success('Questionnaire mis à jour');
      } else {
        await api.post(
          `/evaluation-templates?company_id=${user?.companyId}&created_by=${user?.id}`,
          formData
        );
        toast.success('Questionnaire créé');
      }
      
      setShowCreateDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          question: '',
          description: '',
          category: 'performance',
          type: 'rating',
          required: true,
          weight: 1
        }
      ]
    }));
  };

  const removeQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const updateQuestion = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const getCategoryBadge = (categoryId) => {
    const cat = questionCategories.find(c => c.id === categoryId);
    return cat ? (
      <Badge className={cat.color}>{cat.label}</Badge>
    ) : (
      <Badge className="bg-slate-100 text-slate-700">{categoryId}</Badge>
    );
  };

  const getTargetTypeLabel = (targetType) => {
    const types = {
      all: 'Tous les employés',
      department: 'Par département',
      position: 'Par poste',
      level: 'Par niveau'
    };
    return types[targetType] || targetType;
  };

  if (loading) {
    return (
      <DashboardLayout title="Questionnaires d'évaluation">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Questionnaires d'évaluation">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/performance')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-bold text-slate-900">
                Questionnaires d'évaluation
              </h1>
              <p className="text-slate-500">
                Créez et gérez vos formulaires d'évaluation personnalisés
              </p>
            </div>
          </div>
          <Button onClick={handleCreateNew} className="bg-webtech-blue hover:bg-webtech-blue-hover">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau questionnaire
          </Button>
        </div>

        {/* Templates List */}
        <div className="grid gap-4">
          {templates.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ClipboardList className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">Aucun questionnaire créé</p>
                <Button onClick={handleCreateNew}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer votre premier questionnaire
                </Button>
              </CardContent>
            </Card>
          ) : (
            templates.map((template) => (
              <Card key={template.id} className={`hover:shadow-md transition-shadow ${!template.isActive ? 'opacity-60' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg text-slate-900">{template.name}</h3>
                        {template.isDefault && (
                          <Badge className="bg-webtech-yellow text-webtech-blue">Par défaut</Badge>
                        )}
                        {!template.isActive && (
                          <Badge className="bg-red-100 text-red-700">Inactif</Badge>
                        )}
                        <Badge variant="outline">{getTargetTypeLabel(template.targetType)}</Badge>
                      </div>
                      
                      {template.description && (
                        <p className="text-slate-500 text-sm mb-4">{template.description}</p>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {template.questions?.length || 0} questions
                        </div>
                        <div className="flex items-center gap-2">
                          {[...new Set(template.questions?.map(q => q.category) || [])].slice(0, 3).map(cat => (
                            <span key={cat}>{getCategoryBadge(cat)}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDuplicate(template)}
                        title="Dupliquer"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(template)}
                        title="Modifier"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Modifier le questionnaire' : 'Créer un questionnaire'}
              </DialogTitle>
              <DialogDescription>
                Configurez les questions qui seront posées lors des évaluations
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom du questionnaire *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Évaluation Managers"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="targetType">Cible</Label>
                  <Select
                    value={formData.targetType}
                    onValueChange={(value) => setFormData({ ...formData, targetType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les employés</SelectItem>
                      <SelectItem value="department">Par département</SelectItem>
                      <SelectItem value="position">Par poste</SelectItem>
                      <SelectItem value="level">Par niveau</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description du questionnaire..."
                  rows={2}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Questionnaire par défaut</p>
                  <p className="text-xs text-slate-500">Utilisé quand aucun autre ne correspond</p>
                </div>
                <Switch
                  checked={formData.isDefault}
                  onCheckedChange={(checked) => setFormData({ ...formData, isDefault: checked })}
                />
              </div>
              
              {/* Questions */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-base font-semibold">Questions ({formData.questions.length})</Label>
                  <Button variant="outline" size="sm" onClick={addQuestion}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter une question
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {formData.questions.map((question, index) => (
                    <Card key={index} className="border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex items-center gap-2 text-slate-400 pt-2">
                            <GripVertical className="w-4 h-4" />
                            <span className="text-sm font-medium w-6">{index + 1}.</span>
                          </div>
                          
                          <div className="flex-1 space-y-3">
                            <div className="grid grid-cols-3 gap-3">
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs">Question *</Label>
                                <Input
                                  value={question.question}
                                  onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                                  placeholder="Intitulé de la question"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Catégorie</Label>
                                <Select
                                  value={question.category}
                                  onValueChange={(value) => updateQuestion(index, 'category', value)}
                                >
                                  <SelectTrigger className="h-9">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {questionCategories.map(cat => (
                                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Description</Label>
                                <Input
                                  value={question.description || ''}
                                  onChange={(e) => updateQuestion(index, 'description', e.target.value)}
                                  placeholder="Aide contextuelle"
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Type de réponse</Label>
                                <Select
                                  value={question.type}
                                  onValueChange={(value) => updateQuestion(index, 'type', value)}
                                >
                                  <SelectTrigger className="h-9">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {questionTypes.map(type => (
                                      <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex items-end gap-3">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={question.required}
                                    onChange={(e) => updateQuestion(index, 'required', e.target.checked)}
                                    className="w-4 h-4 rounded border-slate-300"
                                  />
                                  Obligatoire
                                </label>
                              </div>
                            </div>
                          </div>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeQuestion(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {formData.questions.length === 0 && (
                    <div className="text-center py-8 text-slate-400 border-2 border-dashed rounded-lg">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2" />
                      <p>Aucune question ajoutée</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-webtech-blue hover:bg-webtech-blue-hover"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {editingTemplate ? 'Mettre à jour' : 'Créer le questionnaire'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
